import { useState } from 'react';
import toast from 'react-hot-toast';
import { Card, StatCard } from '../components/Card';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmDialog } from '../components/admin/AdminActions';
import { inputClass, secondaryButton } from '../components/admin/AdminUI';
import { useAdminResource } from './directory/useAdminResource';
import { adminRequest, useAdminAction } from './directory/adminRequest';
import { DirectoryError } from './directory/parts';
import { formatDateTime, humanize } from './directory/labels';

interface SubscriptionMetrics {
  totalSubscriptions: number;
  activeSubscriptions: number;
  pastDueSubscriptions: number;
  canceledSubscriptions: number;
  trialingSubscriptions: number;
  subscriptionsByPlan: Array<{ planCode: string; planName: string; count: number }>;
}

interface BillingEvent {
  id: string;
  eventType: string;
  workspaceId: string | null;
  status: string;
  error: string | null;
  skipReason: string | null;
  createdAt: string;
}

/**
 * Billing across ALL workspaces, for a Central admin: subscription counts by
 * status and plan, Stripe webhook events, and the reconciliation run the API
 * already supports (checks every Stripe-linked subscription against Stripe and
 * repairs drift).
 */
export function PlatformBilling() {
  const [version, setVersion] = useState(0);
  const [status, setStatus] = useState('');
  const metrics = useAdminResource<SubscriptionMetrics>(`/subscriptions/metrics?v=${version}`);
  const events = useAdminResource<{ events: BillingEvent[]; total: number }>(
    `/billing/events?limit=20${status ? `&status=${status}` : ''}&v=${version}`,
  );
  const recon = useAdminResource<{ workerRunning: boolean }>(`/billing/reconciliation/status?v=${version}`);
  const [confirming, setConfirming] = useState(false);
  const run = useAdminAction();
  const m = metrics.data;

  return (
    <div className="space-y-6" data-testid="platform-billing">
      {metrics.error ? (
        <DirectoryError error={metrics.error} what="Subscription metrics" onRetry={metrics.reload} />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard label="Subscriptions" value={(m?.totalSubscriptions ?? 0).toLocaleString()} />
          <StatCard label="Active" value={(m?.activeSubscriptions ?? 0).toLocaleString()} status="success" />
          <StatCard label="Trialing" value={(m?.trialingSubscriptions ?? 0).toLocaleString()} />
          <StatCard
            label="Past due"
            value={(m?.pastDueSubscriptions ?? 0).toLocaleString()}
            status={(m?.pastDueSubscriptions ?? 0) > 0 ? 'warning' : 'neutral'}
          />
          <StatCard label="Canceled" value={(m?.canceledSubscriptions ?? 0).toLocaleString()} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Workspaces by plan" description="The plan on record for each subscription.">
          {!m || !Array.isArray(m.subscriptionsByPlan) || m.subscriptionsByPlan.length === 0 ? (
            <p className="text-sm text-slate-500">No subscriptions yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {(m.subscriptionsByPlan ?? []).map((p) => (
                <li key={p.planCode} className="flex justify-between py-2 text-sm">
                  <span className="text-slate-800">
                    {p.planName} <span className="font-mono text-xs text-slate-400">{p.planCode}</span>
                  </span>
                  <span className="font-medium text-slate-900">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card
          title="Stripe reconciliation"
          description="Compares every Stripe-linked subscription with Stripe and repairs any drift."
          actions={
            <button type="button" className={secondaryButton} onClick={() => setConfirming(true)}>
              Run now
            </button>
          }
        >
          <p className="text-sm text-slate-600">
            Scheduled worker:{' '}
            {recon.data ? (
              <StatusBadge status={recon.data.workerRunning ? 'success' : 'neutral'} label={recon.data.workerRunning ? 'Running' : 'Not running here'} />
            ) : (
              '…'
            )}
          </p>
        </Card>
      </div>

      <Card title="Billing events" description="Stripe webhook events, newest first.">
        <div className="mb-3 flex items-center gap-2">
          <label htmlFor="billing-event-status" className="text-sm text-slate-600">
            Status
          </label>
          <select
            id="billing-event-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={`${inputClass} max-w-[12rem]`}
          >
            <option value="">All</option>
            {['pending', 'processed', 'skipped', 'failed'].map((s) => (
              <option key={s} value={s}>
                {humanize(s)}
              </option>
            ))}
          </select>
        </div>
        {events.error ? (
          <DirectoryError error={events.error} what="Billing events" onRetry={events.reload} />
        ) : !events.data || !Array.isArray(events.data.events) || events.data.events.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 py-6 text-center text-sm text-slate-500">
            No billing events{status ? ` with status ${status}` : ''}.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
            <table className="min-w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  {['Event', 'Workspace', 'Status', 'Received'].map((h) => (
                    <th key={h} className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white">
                {events.data.events.map((e) => (
                  <tr key={e.id} className="transition-colors even:bg-slate-50/40 hover:bg-emerald-50/40">
                    <td className="border-b border-slate-100 px-4 py-3.5 font-mono text-xs text-slate-800">{e.eventType}</td>
                    <td className="border-b border-slate-100 px-4 py-3.5 font-mono text-xs text-slate-500">{e.workspaceId ?? '—'}</td>
                    <td className="border-b border-slate-100 px-4 py-3.5">
                      <StatusBadge
                        status={e.status === 'processed' ? 'success' : e.status === 'failed' ? 'error' : 'neutral'}
                        label={humanize(e.status)}
                      />
                      {(e.error || e.skipReason) && <p className="mt-1 text-xs text-slate-500">{e.error ?? e.skipReason}</p>}
                    </td>
                    <td className="border-b border-slate-100 px-4 py-3.5 text-slate-600">{formatDateTime(e.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirming}
        title="Run Stripe reconciliation now?"
        body={<p>Central checks every Stripe-linked subscription against Stripe and repairs what has drifted.</p>}
        confirmLabel="Run now"
        busy={run.busy}
        error={run.error}
        onClose={() => setConfirming(false)}
        onConfirm={async () => {
          const res = await run.run(() =>
            adminRequest<{ checked?: number; repaired?: number; errors?: number }>('POST', '/billing/reconciliation/run'),
          );
          if (res) {
            toast.success(`Reconciliation done: ${res.checked ?? 0} checked, ${res.repaired ?? 0} repaired, ${res.errors ?? 0} errors`);
            setConfirming(false);
            setVersion((n) => n + 1);
          }
        }}
      />
    </div>
  );
}
